// File: src/components/Admin/ExtensionsV2.tsx
import React, { useState, useEffect } from 'react';
import errorLogger from '../../services/errorLogger.service';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardContent,
  CardActions,
  useTheme,
  useMediaQuery,
  Tooltip,
  List,
  InputAdornment,
  FormControlLabel,
  Switch,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Checkbox,
  DialogContentText,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  AssignmentInd as AssignIcon,
  PhoneForwarded as ForwardIcon,
  Voicemail as VoicemailIcon,
  ExpandMore as ExpandMoreIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  CheckCircleOutline as CheckIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
} from '@mui/icons-material';
import { useAuthWithCustomerOverride } from '../../hooks/useAuthWithCustomerOverride';
import api from '../../services/api';
import pinService from '../../services/pin.service';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCustomerFilter } from '../../hooks/useCustomerFilter';
import ChargePreviewDialog from '../Common/ChargePreviewDialog';
import { getCustomerParams as getCustomerParamsUtil, createUrlWithCustomerParams } from '../../utils/customerParams';

interface OutgoingOption {
  id: string;
  number: string;
  name: string;
}

interface TimeCondition {
  id: number;
  name: string;
  days: string;
  start_time: string;
  end_time: string;
  action: string;
  target: string;
  priority: number;
  enabled: boolean;
}

interface FollowMeDestination {
  id?: number;
  destination: string;
  ring_count: number;
  priority: number;
  group_id: number | null;
  active: number;
}

interface Extension {
  id: number;
  extension: string;
  name: string;
  email: string;
  active: boolean;
  rings: number;
  mailbox: string;
  forward_all: string | null;
  forward_all_active: boolean;
  follow1: string | null;
  follow1_active: boolean;
  follow2: string | null;
  follow2_active: boolean;
  cell_number: string | null;
  ring_cell: boolean;
  direct_number: string | null;
  timezone: string;
  directory: boolean;
  outgoing_id: string | null;
  followme_mode_v2?: string;
  followme_v2_dest_count?: number;
  can_edit: boolean;
  can_delete: boolean;
  is_unassigned: boolean;
  has_softphone: boolean;
  softphone_state?: number;
  softphone_text?: string;
  softphone_color?: string;
  softphone_disabled?: boolean;
  site?: string | null;
  department?: string | null;
  outgoing_options?: OutgoingOption[];
  time_conditions?: TimeCondition[];
}


interface ExtensionsProps {
  userOnly?: boolean;
  autoOpenEdit?: boolean;
}

const ExtensionsV2: React.FC<ExtensionsProps> = ({ userOnly = false, autoOpenEdit = false }) => {
  const { member, isLoginAsCustomer } = useAuthWithCustomerOverride();

  // Track the customer ID to prevent unnecessary re-fetches
  const [lastFetchedCustomerId, setLastFetchedCustomerId] = useState<number | null>(null);

  // Create component logger
  const logger = errorLogger.createComponentLogger('Extensions');

  // Already checking for login token in state initialization above

  // Debug logging
  useEffect(() => {
    logger.debug('Auth state:', {
      isLoginAsCustomer,
      memberCustomerID: member?.customerID,
      memberType: member?.memberType,
      originalCustomerId: (member as any)?.originalCustomerId,
      lastFetchedCustomerId
    });
  }, [member, isLoginAsCustomer, lastFetchedCustomerId]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const customerFilter = useCustomerFilter();
  
  // Initialize based on whether we have a login token in URL
  const urlParams = new URLSearchParams(window.location.search);
  const hasLoginToken = urlParams.has('lat');
  const hasSessionData = !!sessionStorage.getItem('loginAsCustomer');
  
  const [loading, setLoading] = useState(true);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);
  const [waitingForLoginAs, setWaitingForLoginAs] = useState(() => {
    // Wait if we have a token but no session data yet
    const shouldWait = hasLoginToken && !hasSessionData;
    if (shouldWait) {
      logger.debug('Initializing with waitingForLoginAs=true due to login token without session data');
    }
    return shouldWait;
  });
  const [extensions, setExtensions] = useState<Extension[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedExtension, setSelectedExtension] = useState<Extension | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Helper to get customer filter params for API calls
  const getCustomerParams = () => {
    return getCustomerParamsUtil({
      isLoginAsCustomer,
      memberCustomerID: member?.customerID,
      customerFilterIsFiltered: customerFilter.isFiltered,
      customerFilterCustomerId: customerFilter.customerId,
      memberType: member?.memberType
    });
  };
  const [expandedAccordion, setExpandedAccordion] = useState<string | false>('basic');
  const [availableMembers, setAvailableMembers] = useState<any[]>([]);
  const [selectedMemberId, setSelectedMemberId] = useState<string>('');
  const [assignLoading, setAssignLoading] = useState(false);
  const [softphonesEnabled, setSoftphonesEnabled] = useState(true); // Default to true
  const [outgoingOptions, setOutgoingOptions] = useState<OutgoingOption[]>([]);
  const [siteDepartmentDialogOpen, setSiteDepartmentDialogOpen] = useState(false);
  const [sites, setSites] = useState<{id: number, name: string}[]>([]);
  const [departments, setDepartments] = useState<{id: number, name: string}[]>([]);
  const [newSite, setNewSite] = useState('');
  const [newDepartment, setNewDepartment] = useState('');

  // V2 Follow-Me state
  const [followMeV2Mode, setFollowMeV2Mode] = useState<'disabled' | 'simultaneous' | 'sequential'>('disabled');
  const [followMeV2Destinations, setFollowMeV2Destinations] = useState<FollowMeDestination[]>([]);
  const [allowRinggroupExpansion, setAllowRinggroupExpansion] = useState(true);
  const [newDestination, setNewDestination] = useState('');
  const [newRingCount, setNewRingCount] = useState(3);
  const [chargePreviewOpen, setChargePreviewOpen] = useState(false);
  const [chargeDetails, setChargeDetails] = useState<any>(null);
  const [chargePreviewLoading, setChargePreviewLoading] = useState(false);
  const [chargePreviewError, setChargePreviewError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // Form state for new extension
  const [newExtensionData, setNewExtensionData] = useState({
    extension: '',
    active: true,
    create_member: false,
    member_fname: '',
    member_lname: '',
    member_email: '',
    member_type: 'member',
    timezone: 'eastern',
    directory: true,
    rings: 4,
    cell_number: '',
    ring_cell: false,
    outgoing_id: '',
    site: '',
    department: ''
  });
  
  // Form state for editing
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    active: true,
    rings: 4,
    forward_all: '',
    forward_all_active: false,
    follow1: '',
    follow1_active: false,
    follow2: '',
    follow2_active: false,
    cell_number: '',
    ring_cell: false,
    timezone: 'eastern',
    directory: true,
    outgoing_id: '',
    site: '',
    department: '',
  });

  useEffect(() => {
    if (!member) return;
    
    // If we're waiting for login-as data, don't do anything
    if (waitingForLoginAs) {
      logger.debug('Still waiting for login-as data, not fetching...');
      return;
    }
    
    // Check if we're still waiting for login-as state to be determined
    const urlParams = new URLSearchParams(window.location.search);
    const hasLoginToken = urlParams.has('lat');
    const sessionHasLoginAs = sessionStorage.getItem('loginAsCustomer') !== null;
    
    // CRITICAL: If we have a login token in URL but no session data yet, DO NOT FETCH
    // Wait for CustomerBanner to retrieve and set the data
    if (hasLoginToken && !sessionHasLoginAs) {
      logger.debug('Have login token but no session data yet, NOT FETCHING, waiting for CustomerBanner...');
      setLoading(true); // Show loading state
      setWaitingForLoginAs(true);
      return; // Don't fetch yet - CustomerBanner will trigger via event
    }
    
    // ADDITIONAL CHECK: If we have session data, verify it matches our current member
    if (sessionHasLoginAs) {
      try {
        const sessionData = JSON.parse(sessionStorage.getItem('loginAsCustomer') || '{}');
        if (sessionData.customerId && sessionData.customerId !== member.customerID) {
          logger.debug('Session has different customer ID than member, waiting for sync...', {
            sessionCustomerId: sessionData.customerId,
            memberCustomerId: member.customerID
          });
          // The hook hasn't updated yet, wait
          return;
        }
      } catch (e) {
        logger.error('Error parsing session data', e, false);
      }
    }
    
    const currentCustomerId = member.customerID;
    
    // Only fetch if customer ID has actually changed
    if (currentCustomerId !== lastFetchedCustomerId) {
      logger.debug('Customer ID changed', { from: lastFetchedCustomerId, to: currentCustomerId });
      
      // Add a longer delay if we might be in login-as mode
      const delay = (hasLoginToken || sessionHasLoginAs) ? 500 : 300;
      
      const timer = setTimeout(() => {
        if (userOnly || ['master_admin', 'reseller_admin', 'admin'].includes(member.memberType)) {
          logger.debug('Fetching data for customer', { customerId: currentCustomerId });
          setLastFetchedCustomerId(currentCustomerId);
          fetchExtensions();
          fetchSitesAndDepartments();
        }
      }, delay);
      
      return () => clearTimeout(timer);
    } else {
      logger.debug('Customer ID unchanged, skipping fetch');
    }
    
    // REMOVED: Automatic refetch on focus - this was causing too many refreshes
    // Users can manually refresh with the refresh button if needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member?.customerID, member?.memberType, lastFetchedCustomerId, userOnly]);

  // REMOVED: Duplicate pathname-based refetch - the main useEffect already handles this
  // This was causing double fetches when navigating

  // Update document title when managing a specific customer
  
  useEffect(() => {
    // Check if we're in login-as mode
    const loginAsData = sessionStorage.getItem('loginAsCustomer');
    if (loginAsData) {
      try {
        const data = JSON.parse(loginAsData);
        document.title = `${data.customerId} - ${data.customerName} - Extensions`;
        logger.debug('Set title for login-as mode', { title: document.title });
        return;
      } catch (e) {
        logger.error('Error parsing loginAsCustomer data for title', e, false);
      }
    }
    
    // Otherwise use customer filter
    if (customerFilter.isFiltered && customerFilter.customerId && customerFilter.customerName) {
      const decodedCustomerName = decodeURIComponent(customerFilter.customerName);
      document.title = `Customer ${customerFilter.customerId} - ${decodedCustomerName} - Extensions - Portal`;
    } else {
      document.title = userOnly ? 'My Extensions - Portal' : 'Extensions - Portal';
    }
  }, [customerFilter, userOnly, isLoginAsCustomer, member]);

  // Auto-open edit dialog for single extension when autoOpenEdit is true
  useEffect(() => {
    if (autoOpenEdit && extensions.length === 1 && !hasAutoOpened && !editDialogOpen && !loading) {
      logger.debug('Auto-opening edit dialog for single extension');
      setHasAutoOpened(true);
      handleEdit(extensions[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenEdit, extensions, hasAutoOpened, editDialogOpen, loading]);

  const fetchExtensions = async () => {
    logger.debug('=== fetchExtensions START ===');
    
    // CRITICAL SAFETY CHECK: Ensure we have the correct customer context
    if (waitingForLoginAs) {
      logger.debug('SAFETY: Still waiting for login-as data, aborting fetch');
      return;
    }
    
    // Double-check sessionStorage if we expect to be in login-as mode
    const urlParams = new URLSearchParams(location.search);
    const hasLoginToken = urlParams.has('lat');
    const sessionData = sessionStorage.getItem('loginAsCustomer');
    
    if (hasLoginToken && !sessionData) {
      logger.debug('SAFETY: Have login token but no session data yet, aborting fetch');
      setWaitingForLoginAs(true);
      return;
    }
    
    logger.debug('Current state:', {
      isLoginAsCustomer,
      memberCustomerID: member?.customerID,
      memberType: member?.memberType,
      originalCustomerId: (member as any)?.originalCustomerId,
      lastFetchedCustomerId,
      customerFilter: customerFilter,
      sessionData: sessionData ? JSON.parse(sessionData) : null
    });
    
    try {
      setLoading(true);
      // Pass userOnly parameter if in userOnly mode
      const params: any = userOnly ? { userOnly: 'true' } : {};
      
      // Add customer filter for master admins and reseller admins
      if (isLoginAsCustomer && member?.customerID) {
        // When in "login as" mode, always use the overridden customer ID
        logger.debug('LOGIN AS MODE - Using customerID', { customerID: member.customerID });
        params.customerId = member.customerID;
        
        // SAFETY: Verify this matches sessionStorage
        if (sessionData) {
          const parsed = JSON.parse(sessionData);
          if (parsed.customerId !== member.customerID) {
            logger.critical('CRITICAL: customerID mismatch!', {
              sessionCustomerId: parsed.customerId,
              memberCustomerId: member.customerID
            });
            // Abort the fetch to prevent wrong data
            setError('Customer context mismatch - please refresh the page');
            setLoading(false);
            return;
          }
        }
      } else if (customerFilter.isFiltered && (member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin')) {
        logger.debug('FILTER MODE - Using customerFilter', { customerId: customerFilter.customerId });
        params.customerId = customerFilter.customerId;
      } else {
        logger.debug('DEFAULT MODE - No customer filter applied, will use JWT customer');
      }
      
      logger.debug('FINAL API PARAMS', params);
      const response = await api.get('/endpoints/extensions', { params });
      logger.debug('API response:', {
        success: response.data.success,
        debug_custid: response.data.debug_custid,
        extensionCount: response.data.data?.extensions?.length || response.data.data?.length || 0
      });
      if (userOnly) {
        logger.debug('Extensions API response (userOnly mode)', response.data);
      }
      if (response.data.success) {
        // Handle new response format with extensions array and softphones_enabled flag
        if (response.data.data && response.data.data.extensions !== undefined) {
          if (userOnly) {
            logger.debug('Using new format with extensions array');
            logger.debug('Extensions received', { extensions: response.data.data.extensions });
            logger.debug('User info', { email: member?.email, extension: member?.extension });
          }
          setExtensions(response.data.data.extensions || []);
          setSoftphonesEnabled(response.data.data.softphones_enabled !== false);
        } else if (response.data.data && Array.isArray(response.data.data)) {
          // Fallback for old format (array directly)
          if (userOnly) {
            logger.debug('Using old format with direct array');
          }
          setExtensions(response.data.data || []);
        } else {
          logger.error('Unexpected response format', response.data, false);
          setExtensions([]);
        }
      }
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load extensions');
      logger.error('Extensions error', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSitesAndDepartments = async () => {
    try {
      const response = await api.get('/endpoints/sites_departments');
      
      if (response.data.success) {
        // Set sites and departments from the database
        setSites(response.data.data.sites);
        setDepartments(response.data.data.departments);
      }
    } catch (err) {
      logger.error('Failed to fetch sites and departments', err);
    }
  };

  const handleEdit = (extension: Extension) => {
    logger.debug('handleEdit called for extension', { extension });
    logger.debug('Phone fields values', {
      forward_all: extension.forward_all,
      follow1: extension.follow1,
      follow2: extension.follow2,
      cell_number: extension.cell_number
    });
    
    // Helper function to convert "0" string to empty string
    const cleanPhoneField = (value: string | null | undefined | number): string => {
      if (value === null || value === undefined || value === '0' || value === 0) {
        return '';
      }
      return String(value);
    };
    
    // For now, just use the existing extension data
    setSelectedExtension(extension);
    setFormData({
      name: extension.name || '',
      email: extension.email || '',
      active: extension.active,
      rings: extension.rings,
      forward_all: cleanPhoneField(extension.forward_all),
      forward_all_active: extension.forward_all_active,
      follow1: cleanPhoneField(extension.follow1),
      follow1_active: extension.follow1_active,
      follow2: cleanPhoneField(extension.follow2),
      follow2_active: extension.follow2_active,
      cell_number: cleanPhoneField(extension.cell_number),
      ring_cell: extension.ring_cell,
      timezone: extension.timezone || 'eastern',
      directory: extension.directory,
      outgoing_id: extension.outgoing_id || '',
      site: extension.site || '',
      department: extension.department || '',
    });
    // Auto-expand Follow-Me accordion if it's active
    if (extension.followme_mode_v2 && extension.followme_mode_v2 !== 'disabled') {
      setExpandedAccordion('followme-v2');
    } else {
      setExpandedAccordion('basic');
    }
    setEditDialogOpen(true);

    // Fetch additional details after dialog opens
    fetchExtensionDetails(extension.extension);
    fetchExtensionV2Data(extension.extension);
  };

  const fetchExtensionDetails = async (extensionNumber: string) => {
    logger.debug('fetchExtensionDetails called for extension', { extensionNumber });
    try {
      // Build params with userOnly and customer filtering
      const params: any = userOnly ? { userOnly: 'true' } : {};
      
      // Add customer filter same as fetchExtensions
      if (isLoginAsCustomer && member?.customerID) {
        params.customerId = member.customerID;
        logger.debug('fetchExtensionDetails - Using loginAs customerID', { customerID: member.customerID });
      } else if (customerFilter.isFiltered && (member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin')) {
        params.customerId = customerFilter.customerId;
        logger.debug('fetchExtensionDetails - Using customerFilter', { customerId: customerFilter.customerId });
      }
      
      logger.debug('fetchExtensionDetails - Final params being sent', params);
      logger.debug('fetchExtensionDetails - Auth info', { isLoginAsCustomer, member });
      const response = await api.get(`/endpoints/extensions/${extensionNumber}`, { params });
      if (response.data.success && response.data.data) {
        // Handle new response format
        let detailedExtension: any;
        if (response.data.data.extension !== undefined) {
          detailedExtension = response.data.data.extension;
          setSoftphonesEnabled(response.data.data.softphones_enabled !== false);
        } else {
          // Fallback for old format
          detailedExtension = response.data.data;
        }
        
        // Update the selected extension with detailed data
        setSelectedExtension(prev => ({
          ...prev!,
          outgoing_options: detailedExtension.outgoing_options || [],
          time_conditions: detailedExtension.time_conditions || []
        }));
        
        // Update form data if needed
        setFormData(prev => ({
          ...prev,
          outgoing_id: detailedExtension.outgoing_id || ''
        }));
      }
    } catch (err: any) {
      logger.error('Failed to fetch extension details', err);
      // Don't show error to user as this is a non-critical enhancement
    }
  };

  // Helper function to build API params with customer filtering
  const buildV2ApiParams = () => {
    const params: any = userOnly ? { userOnly: 'true' } : {};

    if (isLoginAsCustomer && member?.customerID) {
      params.customerId = member.customerID;
    } else if (customerFilter.isFiltered && (member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin')) {
      params.customerId = customerFilter.customerId;
    }

    return params;
  };

  const fetchExtensionV2Data = async (extensionNumber: string) => {
    logger.debug('fetchExtensionV2Data called for extension', { extensionNumber });
    try {
      const params = buildV2ApiParams();
      logger.debug('fetchExtensionV2Data - Final params being sent', params);
      const response = await api.get(`/endpoints/extension-v2/${extensionNumber}`, { params });
      if (response.data.success && response.data.data) {
        const v2Data = response.data.data;
        setFollowMeV2Mode(v2Data.followme_mode_v2 || 'disabled');
        setFollowMeV2Destinations(v2Data.destinations || []);
        setAllowRinggroupExpansion(v2Data.allow_ringgroup_expansion !== 0);
        logger.debug('V2 follow-me data loaded', { mode: v2Data.followme_mode_v2, destinations: v2Data.destinations, allow_ringgroup_expansion: v2Data.allow_ringgroup_expansion });
      }
    } catch (err: any) {
      logger.error('Failed to fetch V2 follow-me data', err);
      // Reset to defaults on error
      setFollowMeV2Mode('disabled');
      setFollowMeV2Destinations([]);
      setAllowRinggroupExpansion(true);
    }
  };

  const handleAddV2Destination = async () => {
    if (!selectedExtension) return;

    // Validate destination format
    if (!/^\d{4}$/.test(newDestination) && !/^\d{10}$/.test(newDestination)) {
      setError('Destination must be a 4-digit extension or 10-digit phone number');
      return;
    }

    // Check max 10 destinations
    if (followMeV2Destinations.length >= 10) {
      setError('Maximum 10 follow-me destinations allowed');
      return;
    }

    try {
      const params = buildV2ApiParams();
      const response = await api.post(`/endpoints/extension-v2/${selectedExtension.extension}/destinations`, {
        destination: newDestination,
        ring_count: newRingCount,
        priority: followMeV2Destinations.length + 1,
        group_id: null
      }, { params });

      if (response.data.success) {
        // Reload V2 data
        await fetchExtensionV2Data(selectedExtension.extension);
        setNewDestination('');
        setNewRingCount(3);
        setSuccess('Destination added successfully');
      }
    } catch (err: any) {
      logger.error('Failed to add V2 destination', err);
      setError(err.response?.data?.message || 'Failed to add destination');
    }
  };

  const handleDeleteV2Destination = async (destId: number) => {
    if (!selectedExtension) return;

    try {
      const params = buildV2ApiParams();
      const response = await api.delete(`/endpoints/extension-v2/${selectedExtension.extension}/destinations/${destId}`, { params });

      if (response.data.success) {
        // Reload V2 data
        await fetchExtensionV2Data(selectedExtension.extension);
        setSuccess('Destination deleted successfully');
      }
    } catch (err: any) {
      logger.error('Failed to delete V2 destination', err);
      setError(err.response?.data?.message || 'Failed to delete destination');
    }
  };

  const handleMoveV2Destination = async (destId: number, direction: 'up' | 'down') => {
    if (!selectedExtension) return;

    const currentIndex = followMeV2Destinations.findIndex(d => d.id === destId);
    if (currentIndex === -1) return;
    if (direction === 'up' && currentIndex === 0) return;
    if (direction === 'down' && currentIndex === followMeV2Destinations.length - 1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    const reordered = [...followMeV2Destinations];
    [reordered[currentIndex], reordered[newIndex]] = [reordered[newIndex], reordered[currentIndex]];

    // Update priorities
    const destinationsWithPriority = reordered.map((dest, index) => ({
      id: dest.id!,
      priority: index + 1
    }));

    try {
      const params = buildV2ApiParams();
      const response = await api.post(`/endpoints/extension-v2/${selectedExtension.extension}/destinations/reorder`, {
        destinations: destinationsWithPriority
      }, { params });

      if (response.data.success) {
        // Reload V2 data
        await fetchExtensionV2Data(selectedExtension.extension);
      }
    } catch (err: any) {
      logger.error('Failed to reorder V2 destinations', err);
      setError(err.response?.data?.message || 'Failed to reorder destinations');
    }
  };

  const handleSaveV2Mode = async () => {
    if (!selectedExtension) return;

    try {
      const params = buildV2ApiParams();
      const response = await api.put(`/endpoints/extension-v2/${selectedExtension.extension}`, {
        followme_mode_v2: followMeV2Mode,
        use_dialplan_v2: followMeV2Mode !== 'disabled' ? 1 : 0
      }, { params });

      if (response.data.success) {
        setSuccess('Follow-Me V2 mode updated successfully');
      }
    } catch (err: any) {
      logger.error('Failed to update V2 mode', err);
      setError(err.response?.data?.message || 'Failed to update Follow-Me V2 mode');
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedExtension) return;
    
    try {
      // Convert empty strings to null for phone number fields
      const dataToSave = {
        ...formData,
        forward_all: formData.forward_all || null,
        follow1: formData.follow1 || null,
        follow2: formData.follow2 || null,
        cell_number: formData.cell_number || null,
        outgoing_id: formData.outgoing_id || null,
        site: formData.site || null,
        department: formData.department || null
      };
      
      // Get customer params and add userOnly if needed
      const params = { ...getCustomerParams(), ...(userOnly ? { userOnly: 'true' } : {}) };
      const response = await api.put(`/endpoints/extensions/${selectedExtension.extension}`, dataToSave, { params });
      
      if (response.data.success) {
        setSuccess('Extension updated successfully');
        setEditDialogOpen(false);
        fetchExtensions();

        // Dispatch event to notify other components about extension update
        const event = new CustomEvent('extensionUpdated', {
          detail: {
            extension: selectedExtension.extension,
            outgoing_id: formData.outgoing_id
          }
        });
        window.dispatchEvent(event);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update extension');
    }
  };

  // Track which extensions are being deleted to prevent double-clicks
  const [deletingExtensions] = useState(() => new Set<string>());
  
  const handleDelete = async (extension: Extension) => {
    logger.debug('handleDelete called', { extension: extension.extension, extensionObject: extension });
    
    // Prevent double-clicks
    if (deletingExtensions.has(extension.extension)) {
      logger.debug('Delete already in progress for this extension');
      return;
    }
    
    // Set the extension to delete and open the detailed confirmation dialog
    setSelectedExtension(extension);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedExtension) return;

    logger.debug('Delete confirmed, proceeding...');
    deletingExtensions.add(selectedExtension.extension);
    setLoading(true);
    setDeleteDialogOpen(false);

    try {
      const params = getCustomerParams();
      logger.debug('Delete - Auth context', {
        isLoginAsCustomer,
        memberCustomerID: member?.customerID,
        customerFilter,
        params
      });

      // CRITICAL: Verify we have the correct customer ID
      const targetCustomerId = params.customerId || member?.customerID;
      logger.critical('CRITICAL DELETE CHECK', {
        targetCustomerId,
        extension: selectedExtension.extension
      });

      // Safety check - if we don't have a clear customer ID, abort
      if (!targetCustomerId) {
        logger.error('ABORT: No customer ID determined for deletion', null, false);
        setError('Unable to determine customer ID for deletion. Operation aborted for safety.');
        setLoading(false);
        deletingExtensions.delete(selectedExtension.extension);
        return;
      }

      // Log to error_logs table for debugging
      await api.post('/endpoints/debug-log', {
        level: 'debug',
        source: 'extensions-delete',
        message: `Attempting to delete extension ${selectedExtension.extension}`,
        context: {
          extension: selectedExtension.extension,
          isLoginAsCustomer,
          memberCustomerID: member?.customerID,
          customerFilterIsFiltered: customerFilter.isFiltered,
          customerFilterCustomerId: customerFilter.customerId,
          customerFilterCustomerName: customerFilter.customerName,
          params,
          memberType: member?.memberType,
        },
        customerId: member?.customerID || customerFilter.customerId,
      }).catch(err => logger.error('Failed to log debug info', err, false));

      const url = createUrlWithCustomerParams(`/endpoints/extensions/${selectedExtension.extension}`, params);
      logger.debug('Making DELETE request', { url });
      const response = await api.delete(url);
      
      logger.debug('Delete response', response.data);
      
      if (response.data.success) {
        // Check for DID warning
        if (response.data.data?.warning) {
          setSuccess(`Extension deleted successfully. Warning: ${response.data.data.warning}`);
        } else {
          setSuccess('Extension deleted successfully');
        }
        // Just refresh from database - no timing needed
        await fetchExtensions();
      }
    } catch (err: any) {
      logger.error('Extension deletion error', {
        error: err,
        responseData: err.response?.data,
        responseStatus: err.response?.status,
        responseHeaders: err.response?.headers
      });
      
      // Check if response is empty (PHP fatal error)
      if (err.response?.status === 500 && !err.response?.data) {
        logger.error('Empty 500 response - likely PHP fatal error', null, false);
        setError('Server error occurred while deleting extension. Please check server logs.');
      } else if (err.response?.status !== 404) {
        const errorMessage = err.response?.data?.message || err.response?.data?.error || 'Failed to delete extension';
        logger.error('Delete error message', { errorMessage }, false);
        setError(errorMessage);
      } else {
        // If 404, the extension is already gone, so refresh the list
        logger.debug('Extension not found (404), refreshing list');
        await fetchExtensions();
      }
    } finally {
      deletingExtensions.delete(selectedExtension.extension);
      setSelectedExtension(null);
      setLoading(false);
    }
  };

  const handleAssign = async (extension: Extension) => {
    setSelectedExtension(extension);
    setSelectedMemberId('');
    setAssignDialogOpen(true);

    // Fetch available members for the correct customer
    try {
      const params = getCustomerParams();
      const url = createUrlWithCustomerParams('/endpoints/extensions/unassigned-members', params);
      const response = await api.get(url);
      if (response.data.success) {
        setAvailableMembers(response.data.data || []);
      }
    } catch (err) {
      logger.error('Failed to fetch available members', err, false);
      setError('Failed to load available members');
    }
  };

  const handleAssignSubmit = async () => {
    if (!selectedExtension || !selectedMemberId) return;
    
    setAssignLoading(true);
    try {
      const params = getCustomerParams();
      const url = createUrlWithCustomerParams(`/endpoints/extensions/${selectedExtension.extension}/assign`, params);
      const response = await api.post(url, {
        member_id: selectedMemberId
      });
      
      if (response.data.success) {
        setSuccess('Extension assigned successfully');
        setAssignDialogOpen(false);
        fetchExtensions(); // Refresh the list
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to assign extension');
    } finally {
      setAssignLoading(false);
    }
  };

  const handleAddExtension = async () => {
    // Validate extension number
    const extNum = parseInt(newExtensionData.extension);
    if (isNaN(extNum) || extNum < 1000 || (extNum >= 8000 && extNum <= 8999)) {
      setError('Extension must be between 1000-7999 or 9000-9999');
      return;
    }

    // Validate required user fields if creating member
    if (newExtensionData.create_member) {
      if (!newExtensionData.member_fname.trim() || 
          !newExtensionData.member_lname.trim() || 
          !newExtensionData.member_email.trim()) {
        setError('All user fields are required when creating a user account');
        return;
      }
    }

    setLoading(true);
    setError(null);
    setChargePreviewError(null);
    
    try {
      // Get customer ID
      const customerId = customerFilter.customerId || member?.customerID;
      
      // Get charge preview
      const params = getCustomerParams();
      const previewUrl = createUrlWithCustomerParams('/endpoints/charge-preview', params);
      const previewResponse = await api.post(previewUrl, {
        service_type: 'extension',
        service_id: newExtensionData.extension,
        monthly_rate: 14.95, // Will be updated by the endpoint based on package
        customerId: customerId
      });
      
      if (previewResponse.data.success) {
        // Map snake_case API response to camelCase for component
        const apiData = previewResponse.data.data;
        setChargeDetails({
          serviceType: apiData.service_type,
          serviceId: apiData.service_id,
          description: apiData.description,
          monthlyRate: apiData.monthly_rate,
          proratedDays: apiData.prorated_days,
          proratedAmount: apiData.prorated_amount,
          currentMonthCharge: apiData.current_month_charge,
          nextMonthCharge: apiData.next_month_charge,
          subtotal: apiData.subtotal,
          taxDetails: apiData.tax_details,
          totalTax: apiData.total_tax,
          totalCharge: apiData.total_charge,
          currentBalance: apiData.current_balance,
          newBalance: apiData.new_balance,
          sufficientFunds: apiData.sufficient_funds,
          billingPeriod: apiData.billing_period,
          includedInPackage: apiData.included_in_package,
          province: apiData.province,
          provinceName: apiData.province_name
        });
        setChargePreviewOpen(true);
        setLoading(false);
      }
    } catch (err: any) {
      logger.error('Extension charge preview error', err);
      setError(err.response?.data?.message || 'Failed to calculate charges');
      setLoading(false);
    }
  };

  const handleChargePreviewConfirm = async () => {
    try {
      setChargePreviewLoading(true);
      setChargePreviewError(null);
      const params = getCustomerParams();
      const createUrl = createUrlWithCustomerParams('/endpoints/extensions', params);
      const response = await api.post(createUrl, {
          extension: newExtensionData.extension,
          active: newExtensionData.active,
          create_member: newExtensionData.create_member,
          member_fname: newExtensionData.member_fname.trim(),
          member_lname: newExtensionData.member_lname.trim(),
          member_email: newExtensionData.member_email.trim(),
          member_type: newExtensionData.member_type,
          timezone: newExtensionData.timezone,
          directory: newExtensionData.directory,
          rings: newExtensionData.rings,
          cell_number: newExtensionData.cell_number.trim(),
          ring_cell: newExtensionData.ring_cell,
          outgoing_id: newExtensionData.outgoing_id || null,
          site: newExtensionData.site || null,
          department: newExtensionData.department || null
        });
      
      if (response.data.success) {
        const newExtNumber = response.data.extension || newExtensionData.extension;
        let successMsg = `Extension ${newExtNumber} created successfully`;
        
        if (chargeDetails?.includedInPackage) {
          successMsg += ' (Included in your package - no charge)';
        } else if (response.data.billed) {
          successMsg += ` - Charged ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(response.data.amount_charged || chargeDetails?.totalCharge || 0)}`;
        }
        
        if (newExtensionData.create_member) {
          successMsg += `. User account created for ${newExtensionData.member_fname} ${newExtensionData.member_lname}`;
        }
        
        setSuccess(successMsg);
        setAddDialogOpen(false);
        setChargePreviewOpen(false);
        setChargeDetails(null);
        setNewExtensionData({
          extension: '',
          active: true,
          create_member: false,
          member_fname: '',
          member_lname: '',
          member_email: '',
          member_type: 'member',
          timezone: 'eastern',
          directory: true,
          rings: 4,
          cell_number: '',
          ring_cell: false,
          outgoing_id: '',
          site: '',
          department: ''
        });
        await fetchExtensions(); // Refresh the list
      }
    } catch (err: any) {
      logger.error('Extension creation error', err);
      setChargePreviewError(err.response?.data?.message || 'Failed to create extension');
    } finally {
      setChargePreviewLoading(false);
    }
  };

  const handleChargePreviewClose = () => {
    setChargePreviewOpen(false);
    setChargeDetails(null);
    setChargePreviewError(null);
    setLoading(false);
  };

  const handleAddFunds = () => {
    setChargePreviewOpen(false);
    navigate('/billing');
  };

  const handleResetPin = async (type: 'voicemail' | 'softphone') => {
    if (!selectedExtension) return;
    
    const pinType = type === 'voicemail' ? 'Voicemail' : 'Softphone';
    
    if (!window.confirm(`Are you sure you want to reset the ${pinType} PIN for extension ${selectedExtension.extension}?`)) {
      return;
    }
    
    try {
      let response;
      if (type === 'voicemail') {
        response = await pinService.resetVoicemailPin(selectedExtension.extension);
      } else {
        response = await pinService.resetSoftphonePin(selectedExtension.extension);
      }
      
      if (response.success) {
        if (type === 'voicemail') {
          const emailNote = response.email_sent ? ' New PIN will be sent to the user.' : '';
          setSuccess(`Voicemail PIN reset successfully.${emailNote} The temporary PIN is their extension number (${selectedExtension.extension}). By dialing *98 and then entering ${selectedExtension.extension}, they will go through a setup process to set recordings and change their PIN. IMPORTANT: They must set a 4-digit PIN that is NOT their extension number to avoid re-entering setup each time.`);
        } else {
          const emailNote = response.email_sent ? ' New PIN will be sent to the user.' : ' (No email configured)';
          setSuccess(`${pinType} PIN reset successfully.${emailNote}`);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to reset ${pinType} PIN`);
    }
  };

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  const getStatusColor = (active: boolean) => {
    return active ? 'success' : 'error';
  };

  const getSoftphoneStatusChip = (extension: Extension) => {
    if (!extension.has_softphone) {
      return <Chip label="No Softphone" size="small" color="default" />;
    }
    
    // Show disabled state with subtle gray color
    if (extension.softphone_disabled) {
      return (
        <Chip 
          label={extension.softphone_text || 'Offline'}
          size="small"
          sx={{ 
            bgcolor: 'grey.300',
            color: 'grey.700'
          }}
        />
      );
    }
    
    return (
      <Chip 
        label={extension.softphone_text || 'Unknown'}
        size="small"
        sx={{
          bgcolor: 
            extension.softphone_color === 'green' ? 'success.light' :
            extension.softphone_color === 'yellow' ? 'warning.light' :
            extension.softphone_color === 'red' ? 'error.light' :
            extension.softphone_color === 'blue' ? 'info.light' :
            extension.softphone_color === 'orange' ? 'warning.light' :
            'grey.300',
          color:
            extension.softphone_color === 'green' ? 'success.dark' :
            extension.softphone_color === 'yellow' ? 'warning.dark' :
            extension.softphone_color === 'red' ? 'error.dark' :
            extension.softphone_color === 'blue' ? 'info.dark' :
            extension.softphone_color === 'orange' ? 'warning.dark' :
            'text.secondary',
        }}
      />
    );
  };

  // Filter extensions based on userOnly mode and search term
  const filteredExtensions = (Array.isArray(extensions) ? extensions : []).filter(ext => {
    // If userOnly mode, only show the current user's extension
    if (userOnly && member) {
      // Check if this extension belongs to the current user
      // Use either email or extension number to match
      const isUserExtension = ext.email === member.email || 
                             (member.extension && ext.extension === member.extension);
      if (!isUserExtension) {
        return false;
      }
    }
    
    // Apply search filter
    return ext.extension.includes(searchTerm) ||
      ext.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ext.email?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const renderEditContent = () => (
    <Box sx={{ p: 1 }}>
      {/* Hidden extension field for form submission */}
      <input type="hidden" value={selectedExtension?.extension || ''} />
      
      {/* Basic Information - Always visible */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Rings before Voicemail</InputLabel>
            <Select
              value={formData.rings}
              onChange={(e) => setFormData({ ...formData, rings: Number(e.target.value) })}
              label="Rings before Voicemail"
              sx={{
                '& .MuiSelect-select': {
                  textAlign: 'center'
                }
              }}
            >
              {[2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                <MenuItem key={num} value={num} sx={{ justifyContent: 'center' }}>
                  {num}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="Cell Number"
            value={formData.cell_number || ''}
            onChange={(e) => setFormData({ ...formData, cell_number: e.target.value })}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth size="small">
            <InputLabel>Outgoing Caller ID</InputLabel>
            <Select
              value={formData.outgoing_id || ''}
              onChange={(e) => setFormData({ ...formData, outgoing_id: e.target.value })}
              label="Outgoing Caller ID"
            >
              <MenuItem value="">
                <em>Extension Default</em>
              </MenuItem>
              {selectedExtension?.outgoing_options?.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.number} - {option.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedExtension?.direct_number && (
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
              Direct Number: {selectedExtension.direct_number}
            </Typography>
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel sx={{ fontSize: '0.75rem' }}>Timezone</InputLabel>
            <Select
              value={formData.timezone}
              onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
              label="Timezone"
              size="small"
              sx={{ 
                '& .MuiInputBase-root': {
                  height: 'auto',
                },
                '& .MuiSelect-select': {
                  py: '12.5px',
                  fontSize: '0.75rem',
                  minHeight: '20px',
                }
              }}
            >
              <MenuItem value="newfoundland">Newfoundland Time</MenuItem>
              <MenuItem value="atlantic">Atlantic Time</MenuItem>
              <MenuItem value="eastern">Eastern Time</MenuItem>
              <MenuItem value="central">Central Time</MenuItem>
              <MenuItem value="mountain">Mountain Time</MenuItem>
              <MenuItem value="pacific">Pacific Time</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box display="flex" gap={1}>
            <Button
              size="small"
              variant="outlined"
              fullWidth
              onClick={() => handleResetPin('voicemail')}
              sx={{ fontSize: '0.75rem' }}
            >
              Reset Voicemail PIN
            </Button>
            {softphonesEnabled && (
              <Button
                size="small"
                variant="outlined"
                fullWidth
                onClick={() => handleResetPin('softphone')}
                sx={{ fontSize: '0.75rem' }}
              >
                Reset Softphone PIN
              </Button>
            )}
          </Box>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      {/* Accordion Sections */}
      {/* Follow-Me Accordion */}
      <Accordion expanded={expandedAccordion === 'followme-v2'} onChange={handleAccordionChange('followme-v2')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography>Follow-Me</Typography>
            <Chip
              label={followMeV2Mode === 'disabled' ? 'Disabled' : followMeV2Mode}
              size="small"
              color={followMeV2Mode === 'disabled' ? 'default' : 'primary'}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Mode Selector */}
            <Box>
              <FormControl fullWidth size="small">
                <InputLabel>Follow-Me Mode</InputLabel>
                <Select
                  value={followMeV2Mode}
                  label="Follow-Me Mode"
                  onChange={async (e) => {
                    const newMode = e.target.value as 'disabled' | 'simultaneous' | 'sequential';

                    // Validate: can't enable Follow-Me without at least one destination
                    if (newMode !== 'disabled' && followMeV2Destinations.length === 0) {
                      setError('Please add at least one Follow-Me destination before enabling Follow-Me');
                      return;
                    }

                    setFollowMeV2Mode(newMode);
                    // Auto-save the mode change
                    if (selectedExtension) {
                      try {
                        const params = buildV2ApiParams();
                        const response = await api.put(`/endpoints/extension-v2/${selectedExtension.extension}`, {
                          followme_mode_v2: newMode,
                          use_dialplan_v2: newMode !== 'disabled' ? 1 : 0
                        }, { params });

                        if (response.data.success) {
                          setSuccess('Follow-Me mode updated successfully');
                        }
                      } catch (err: any) {
                        logger.error('Failed to update V2 mode', err);
                        setError(err.response?.data?.message || 'Failed to update Follow-Me mode');
                      }
                    }
                  }}
                >
                  <MenuItem value="disabled">Disabled</MenuItem>
                  <MenuItem value="simultaneous">Simultaneous (Ring All)</MenuItem>
                  <MenuItem value="sequential">Sequential (One After Another)</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Destinations List */}
            {followMeV2Mode !== 'disabled' && (
              <>
                <Divider />
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Destinations ({followMeV2Destinations.length}/10)
                  </Typography>

                  {/* Add New Destination */}
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <TextField
                      size="small"
                      label="Extension or Phone"
                      value={newDestination}
                      onChange={(e) => setNewDestination(e.target.value)}
                      placeholder="1234 or 2893147818"
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      size="small"
                      type="number"
                      label="Rings"
                      value={newRingCount}
                      onChange={(e) => setNewRingCount(Number(e.target.value))}
                      inputProps={{ min: 1, max: 10 }}
                      sx={{ width: 80 }}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={handleAddV2Destination}
                      disabled={followMeV2Destinations.length >= 10}
                    >
                      Add
                    </Button>
                  </Box>

                  {/* Existing Destinations */}
                  {followMeV2Destinations.length === 0 ? (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      No destinations configured. Add one above to get started.
                    </Alert>
                  ) : (
                    <List dense>
                      {followMeV2Destinations.map((dest, index) => (
                        <ListItem
                          key={dest.id}
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            mb: 1,
                            backgroundColor: 'background.paper'
                          }}
                          secondaryAction={
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={() => handleMoveV2Destination(dest.id!, 'up')}
                                disabled={index === 0}
                              >
                                <ArrowUpIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleMoveV2Destination(dest.id!, 'down')}
                                disabled={index === followMeV2Destinations.length - 1}
                              >
                                <ArrowDownIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteV2Destination(dest.id!)}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          }
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <Chip label={`#${index + 1}`} size="small" />
                                <Typography variant="body2" fontWeight="bold">
                                  {dest.destination}
                                </Typography>
                              </Box>
                            }
                            secondary={`${dest.ring_count} ring${dest.ring_count !== 1 ? 's' : ''} (${dest.ring_count * 6} seconds)`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}

                  {/* Help Text */}
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="caption" display="block" gutterBottom>
                      <strong>Simultaneous:</strong> All destinations ring at the same time.
                    </Typography>
                    <Typography variant="caption" display="block">
                      <strong>Sequential:</strong> Destinations ring one after another in order.
                    </Typography>
                  </Alert>
                </Box>

                {/* Ring Group Expansion Control */}
                <Divider sx={{ mt: 2 }} />
                <Box sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={allowRinggroupExpansion}
                        onChange={async (e) => {
                          const newValue = e.target.checked;
                          setAllowRinggroupExpansion(newValue);

                          // Auto-save the setting
                          if (selectedExtension) {
                            try {
                              const params = buildV2ApiParams();
                              const response = await api.put(`/endpoints/extension-v2/${selectedExtension.extension}`, {
                                allow_ringgroup_expansion: newValue ? 1 : 0
                              }, { params });

                              if (response.data.success) {
                                setSuccess('Ring group setting updated successfully');
                              }
                            } catch (err: any) {
                              logger.error('Failed to update ring group setting', err);
                              setError(err.response?.data?.message || 'Failed to update ring group setting');
                              // Revert on error
                              setAllowRinggroupExpansion(!newValue);
                            }
                          }
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">
                          Allow my Follow-Me destinations in ring groups
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          When enabled, ring groups will include your cell and Follow-Me destinations
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              </>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={formData.directory}
              onChange={(e) => setFormData({ ...formData, directory: e.target.checked })}
            />
          }
          label="Show in Directory"
        />
      </Box>

    </Box>
  );


  // Track if we need to refresh on next visibility
  const [needsRefresh, setNeedsRefresh] = useState(false);
  
  // Listen for user deletion events from other components
  useEffect(() => {
    const handleUserDeleted = (event: Event) => {
      const customEvent = event as CustomEvent;
      logger.debug('User deleted event received', customEvent.detail);
      
      // If component is visible, refresh immediately
      if (document.visibilityState === 'visible') {
        // Add a delay to ensure the database has been updated
        setTimeout(() => {
          logger.debug('Refreshing extensions after user deletion');
          if (member && (userOnly || ['master_admin', 'reseller_admin', 'admin'].includes(member.memberType))) {
            fetchExtensions();
          }
        }, 1000); // 1 second delay to ensure DB is updated
      } else {
        // Mark that we need to refresh when tab becomes visible
        logger.debug('Tab not visible, will refresh when visible');
        setNeedsRefresh(true);
      }
    };
    
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'userDeleted' && event.newValue) {
        logger.debug('User deleted via storage event', { newValue: event.newValue });
        
        // If component is visible, refresh immediately
        if (document.visibilityState === 'visible') {
          // Add a delay to ensure the database has been updated
          setTimeout(() => {
            logger.debug('Refreshing extensions after user deletion (storage)');
            if (member && (userOnly || ['master_admin', 'reseller_admin', 'admin'].includes(member.memberType))) {
              fetchExtensions();
            }
          }, 1000); // 1 second delay to ensure DB is updated
        } else {
          // Mark that we need to refresh when tab becomes visible
          logger.debug('Tab not visible, will refresh when visible');
          setNeedsRefresh(true);
        }
      }
    };
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && needsRefresh) {
        logger.debug('Tab became visible, refreshing due to pending user deletion');
        setNeedsRefresh(false);
        if (member && (userOnly || ['master_admin', 'reseller_admin', 'admin'].includes(member.memberType))) {
          fetchExtensions();
        }
      }
    };
    
    // Listen for login as customer changes
    const handleLoginAsChange = (event: CustomEvent) => {
      logger.debug('loginAsCustomerChanged event received, will refresh after member updates');
      // Clear waiting state
      setWaitingForLoginAs(false);
      
      // Update tab title immediately with the customer data
      if (event.detail && event.detail.customerId && event.detail.customerName) {
        document.title = `${event.detail.customerId} - ${event.detail.customerName} - Extensions`;
        logger.debug('Updated tab title', { title: document.title });
      }
      
      // Force immediate refresh with new customer data
      setLastFetchedCustomerId(null); // Reset to force fetch
      // Wait a tiny bit for the member to update, then fetch
      setTimeout(() => {
        if (member && (userOnly || ['master_admin', 'reseller_admin', 'admin'].includes(member.memberType))) {
          logger.debug('Fetching after loginAsCustomerChanged event');
          fetchExtensions();
        }
      }, 50);
    };
    
    // Listen for both custom events and storage events
    window.addEventListener('userDeleted', handleUserDeleted);
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('loginAsCustomerChanged', handleLoginAsChange as EventListener);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('userDeleted', handleUserDeleted);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('loginAsCustomerChanged', handleLoginAsChange as EventListener);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member, userOnly, needsRefresh]);
  
  // Remove permission check when in userOnly mode
  if (!userOnly && member && !['master_admin', 'reseller_admin', 'admin'].includes(member.memberType)) {
    return (
      <Alert severity="error">
        You do not have permission to access this section.
      </Alert>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4" component="h1">
            {userOnly ? 'My Extension Settings' : 'Extension Management'}
          </Typography>
          <Box>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchExtensions}
              sx={{ mr: userOnly ? 0 : 2 }}
            >
              Refresh
            </Button>
            {!userOnly && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={async () => {
                  // Fetch outgoing options from any existing extension
                  try {
                    // Get outgoing options from the first extension (they should all have the same options)
                    if (extensions.length > 0) {
                      const params: any = {};
                      // Add customer filter
                      if (isLoginAsCustomer && member?.customerID) {
                        params.customerId = member.customerID;
                      } else if (customerFilter.isFiltered && (member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin')) {
                        params.customerId = customerFilter.customerId;
                      }
                      const response = await api.get(`/endpoints/extensions/${extensions[0].extension}`, { params });
                      if (response.data.success && response.data.data) {
                        let detailedExtension: any;
                        if (response.data.data.extension !== undefined) {
                          detailedExtension = response.data.data.extension;
                        } else {
                          detailedExtension = response.data.data;
                        }
                        const options = detailedExtension.outgoing_options || [];
                        setOutgoingOptions(options);
                      }
                    }
                  } catch (err) {
                    logger.error('Failed to fetch outgoing options', err, false);
                  }
                  
                  // Find next available extension
                  const extensionNumbers = extensions.map(ext => parseInt(ext.extension)).filter(num => !isNaN(num));
                  let nextExtension = 1000;
                  if (extensionNumbers.length > 0) {
                    const maxExtension = Math.max(...extensionNumbers);
                    nextExtension = maxExtension + 1;
                    // Skip 8000-8999 range
                    if (nextExtension >= 8000 && nextExtension <= 8999) {
                      nextExtension = 9000;
                    }
                  }
                  
                  setNewExtensionData(prev => ({
                    ...prev,
                    extension: nextExtension.toString()
                  }));
                  setAddDialogOpen(true);
                }}
              >
                Add Extension
              </Button>
            )}
          </Box>
        </Box>

        {/* Search Bar - hide in userOnly mode since there's only one extension */}
        {!userOnly && (
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search by extension, name, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        )}

        {/* Error/Success Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Extensions Table/List */}
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : filteredExtensions.length === 0 ? (
          <Box p={4} textAlign="center">
            <Typography variant="body1" color="textSecondary">
              {userOnly ? 'You do not have an extension assigned. Please contact your administrator.' : 'No extensions found.'}
            </Typography>
          </Box>
        ) : isMobile ? (
          // Mobile Card View
          <List>
            {filteredExtensions.map((extension) => (
              <Card key={extension.id} sx={{ mb: 2 }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">
                      {extension.extension}
                      {extension.is_unassigned && (
                        <Chip label="Unassigned" size="small" sx={{ ml: 1 }} />
                      )}
                    </Typography>
                    <Chip
                      label={extension.active ? 'Active' : 'Inactive'}
                      color={getStatusColor(extension.active)}
                      size="small"
                    />
                  </Box>
                  
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    <PersonIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                    {extension.name || 'Unnamed Extension'}
                  </Typography>
                  
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    <EmailIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                    {extension.email || 'No email'}
                  </Typography>
                  
                  <Box display="flex" alignItems="center" gap={1} mt={1}>
                    <VoicemailIcon sx={{ fontSize: 16 }} />
                    <Typography variant="body2">Rings: {extension.rings}</Typography>
                  </Box>
                  
                  {(extension.site || extension.department) && (
                    <Box mt={1}>
                      {extension.site && (
                        <Typography variant="body2" color="textSecondary">
                          Site: {extension.site}
                        </Typography>
                      )}
                      {extension.department && (
                        <Typography variant="body2" color="textSecondary">
                          Department: {extension.department}
                        </Typography>
                      )}
                    </Box>
                  )}
                  
                  {extension.forward_all_active && (
                    <Box display="flex" alignItems="center" gap={1} mt={1}>
                      <ForwardIcon sx={{ fontSize: 16 }} />
                      <Typography variant="body2">Forward: {extension.forward_all}</Typography>
                    </Box>
                  )}
                  
                  <Box mt={1}>
                    {getSoftphoneStatusChip(extension)}
                  </Box>
                </CardContent>
                <CardActions>
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/time-conditions?extension=${extension.extension}`)}
                    color="primary"
                  >
                    <ScheduleIcon fontSize="small" />
                  </IconButton>
                  {(extension.can_edit || userOnly) && (
                    <Button size="small" onClick={() => handleEdit(extension)}>
                      Edit
                    </Button>
                  )}
                  {!userOnly && extension.can_delete && (
                    <Button size="small" color="error" onClick={() => handleDelete(extension)} disabled={loading}>
                      Delete
                    </Button>
                  )}
                  {!userOnly && extension.is_unassigned && (
                    <Button size="small" color="success" onClick={() => handleAssign(extension)}>
                      Assign
                    </Button>
                  )}
                </CardActions>
              </Card>
            ))}
          </List>
        ) : (
          // Desktop Table View
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 70, padding: '6px 8px' }}>Ext</TableCell>
                  <TableCell sx={{ minWidth: 100, padding: '6px 8px' }}>Name</TableCell>
                  <TableCell sx={{ minWidth: 130, padding: '6px 8px' }}>Email</TableCell>
                  <TableCell sx={{ minWidth: 70, padding: '6px 8px' }}>Status</TableCell>
                  <TableCell sx={{ minWidth: 80, padding: '6px 8px' }}>Site</TableCell>
                  <TableCell sx={{ minWidth: 80, padding: '6px 8px' }}>Department</TableCell>
                  <TableCell sx={{ minWidth: 100, padding: '6px 8px' }}>Direct</TableCell>
                  <TableCell sx={{ minWidth: 80, padding: '6px 8px' }}>Forward</TableCell>
                  <TableCell sx={{ minWidth: 90, padding: '6px 8px' }}>Softphone</TableCell>
                  <TableCell align="right" sx={{ minWidth: 100, padding: '6px 8px' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredExtensions.map((extension) => (
                  <TableRow key={extension.id}>
                    <TableCell sx={{ padding: '4px 8px' }}>
                      {extension.extension}
                      {extension.is_unassigned && (
                        <Chip label="Unassigned" size="small" sx={{ ml: 0.5, fontSize: '0.7rem', height: 18 }} />
                      )}
                    </TableCell>
                    <TableCell sx={{ padding: '4px 8px' }}>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 120, fontSize: '0.875rem' }}>
                        {extension.name || 'Unnamed'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ padding: '4px 8px' }}>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 150, fontSize: '0.875rem' }}>
                        {extension.email || 'No email'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ padding: '4px 8px' }}>
                      <Chip
                        label={extension.active ? 'Active' : 'Inactive'}
                        color={getStatusColor(extension.active)}
                        size="small"
                        sx={{ height: 20, fontSize: '0.75rem' }}
                      />
                    </TableCell>
                    <TableCell sx={{ padding: '4px 8px' }}>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 80, fontSize: '0.875rem' }}>
                        {extension.site || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ padding: '4px 8px' }}>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 80, fontSize: '0.875rem' }}>
                        {extension.department || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ padding: '4px 8px' }}>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 120, fontSize: '0.875rem' }}>
                        {extension.direct_number || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ padding: '4px 8px' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                        {extension.forward_all_active && (
                          <Typography variant="body2" noWrap sx={{ maxWidth: 100, fontSize: '0.875rem' }}>
                            Fwd: {extension.forward_all}
                          </Typography>
                        )}
                        {extension.followme_mode_v2 && extension.followme_mode_v2 !== 'disabled' && (
                          <Chip
                            label={`FM: ${extension.followme_mode_v2 === 'simultaneous' ? 'Sim' : 'Seq'} (${extension.followme_v2_dest_count || 0})`}
                            size="small"
                            color="primary"
                            sx={{ height: 18, fontSize: '0.7rem', '& .MuiChip-label': { px: 0.75, py: 0 } }}
                          />
                        )}
                        {!extension.forward_all_active && (!extension.followme_mode_v2 || extension.followme_mode_v2 === 'disabled') && (
                          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>-</Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ padding: '4px 8px' }}>
                      {getSoftphoneStatusChip(extension)}
                    </TableCell>
                    <TableCell align="right" sx={{ padding: '4px 8px' }}>
                      <Box display="flex" justifyContent="flex-end" gap={0.5}>
                        {!userOnly && extension.is_unassigned && (
                          <Tooltip title="Assign">
                            <IconButton
                              size="small"
                              onClick={() => handleAssign(extension)}
                              color="success"
                            >
                              <AssignIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {(extension.can_edit || userOnly) && (
                          <Tooltip title="Time Conditions">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/time-conditions?extension=${extension.extension}`)}
                              color="primary"
                            >
                              <ScheduleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {(extension.can_edit || userOnly) && (
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(extension)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {!userOnly && extension.can_delete && (
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(extension)}
                              color="error"
                              disabled={loading}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Edit Extension Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                Edit Extension {selectedExtension?.extension}
              </Typography>
              <Paper 
                variant="outlined" 
                sx={{ 
                  px: 1.5, 
                  py: 0.5, 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: 1,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'grey.100' }
                }}
                onClick={() => setSiteDepartmentDialogOpen(true)}
              >
                <Box>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>
                    Site: {formData.site || 'Not Set'}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', lineHeight: 1.2, display: 'block' }}>
                    Dept: {formData.department || 'Not Set'}
                  </Typography>
                </Box>
                <EditIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              </Paper>
            </Box>
            <Box display="flex" gap={2} alignItems="center">
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={formData.ring_cell}
                    onChange={(e) => setFormData({ ...formData, ring_cell: e.target.checked })}
                  />
                }
                label="Ring Cell"
                labelPlacement="start"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    color={formData.active ? "success" : "default"}
                  />
                }
                label={formData.active ? "Active" : "Inactive"}
                labelPlacement="start"
              />
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {renderEditContent()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained" color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Extension Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => {
          setAddDialogOpen(false);
          setNewExtensionData({
            extension: '',
            active: true,
            create_member: false,
            member_fname: '',
            member_lname: '',
            member_email: '',
            member_type: 'member',
            timezone: 'eastern',
            directory: true,
            rings: 4,
            cell_number: '',
            ring_cell: false,
            outgoing_id: '',
            site: '',
            department: ''
          });
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                Add New Extension
              </Typography>
              <Paper 
                variant="outlined" 
                sx={{ 
                  px: 1.5, 
                  py: 0.5, 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: 1,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'grey.100' }
                }}
                onClick={() => setSiteDepartmentDialogOpen(true)}
              >
                <Box>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>
                    Site: {newExtensionData.site || 'Not Set'}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', lineHeight: 1.2, display: 'block' }}>
                    Dept: {newExtensionData.department || 'Not Set'}
                  </Typography>
                </Box>
                <EditIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              </Paper>
            </Box>
            <Box />
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {/* Compact layout with all sections visible */}
            <Grid container spacing={2}>
              {/* Left Column */}
              <Grid item xs={12} md={6}>
                {/* Extension Info */}
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Extension Information</Typography>
                <TextField
                  fullWidth
                  label="Extension Number"
                  value={newExtensionData.extension}
                  onChange={(e) => setNewExtensionData({ ...newExtensionData, extension: e.target.value })}
                  required
                  autoFocus
                  size="small"
                  inputProps={{ 
                    maxLength: 4,
                    pattern: '[0-9]{4}'
                  }}
                  helperText="Suggested next available"
                  sx={{ mb: 2 }}
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={newExtensionData.active}
                      onChange={(e) => setNewExtensionData({ ...newExtensionData, active: e.target.checked })}
                      color="success"
                      size="small"
                    />
                  }
                  label={newExtensionData.active ? "Active" : "Inactive"}
                  sx={{ mb: 2 }}
                />

                {/* Extension Settings */}
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Settings</Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Timezone</InputLabel>
                      <Select
                        value={newExtensionData.timezone}
                        onChange={(e) => setNewExtensionData({ ...newExtensionData, timezone: e.target.value })}
                        label="Timezone"
                      >
                        <MenuItem value="newfoundland">Newfoundland</MenuItem>
                        <MenuItem value="atlantic">Atlantic</MenuItem>
                        <MenuItem value="eastern">Eastern</MenuItem>
                        <MenuItem value="central">Central</MenuItem>
                        <MenuItem value="mountain">Mountain</MenuItem>
                        <MenuItem value="pacific">Pacific</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Rings</InputLabel>
                      <Select
                        value={newExtensionData.rings}
                        onChange={(e) => setNewExtensionData({ ...newExtensionData, rings: Number(e.target.value) })}
                        label="Rings"
                      >
                        {[2, 3, 4, 5, 6, 7, 8].map(num => (
                          <MenuItem key={num} value={num}>{num}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={newExtensionData.directory}
                      onChange={(e) => setNewExtensionData({ ...newExtensionData, directory: e.target.checked })}
                      size="small"
                    />
                  }
                  label="Include in Directory"
                  sx={{ mt: 1, mb: 2 }}
                />

                {/* Phone Settings */}
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Phone Numbers</Typography>
                <TextField
                  fullWidth
                  label="Cell Phone"
                  value={newExtensionData.cell_number}
                  onChange={(e) => setNewExtensionData({ ...newExtensionData, cell_number: e.target.value })}
                  size="small"
                  inputProps={{ 
                    maxLength: 10,
                    pattern: '[0-9]{10}'
                  }}
                  placeholder="10 digits"
                  sx={{ mb: 1 }}
                />
                
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newExtensionData.ring_cell}
                      onChange={(e) => setNewExtensionData({ ...newExtensionData, ring_cell: e.target.checked })}
                      size="small"
                    />
                  }
                  label="Ring Cell Phone"
                  sx={{ mb: 1 }}
                />

                <FormControl fullWidth size="small">
                  <InputLabel>Outgoing Number</InputLabel>
                  <Select
                    value={newExtensionData.outgoing_id}
                    onChange={(e) => setNewExtensionData({ ...newExtensionData, outgoing_id: e.target.value })}
                    label="Outgoing Number"
                  >
                    <MenuItem value="">-- Default --</MenuItem>
                    {outgoingOptions.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.number} ({option.name})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Right Column - User Account */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>User Account (Optional)</Typography>
                <Alert severity="info" sx={{ mb: 1, py: 0.5 }}>
                  <Typography variant="caption">
                    Create a user account to allow portal access
                  </Typography>
                </Alert>
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={newExtensionData.create_member}
                      onChange={(e) => setNewExtensionData({ ...newExtensionData, create_member: e.target.checked })}
                      size="small"
                    />
                  }
                  label="Create user account"
                  sx={{ mb: 2 }}
                />

                {newExtensionData.create_member && (
                  <>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="First Name"
                          value={newExtensionData.member_fname}
                          onChange={(e) => setNewExtensionData({ ...newExtensionData, member_fname: e.target.value })}
                          required={newExtensionData.create_member}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Last Name"
                          value={newExtensionData.member_lname}
                          onChange={(e) => setNewExtensionData({ ...newExtensionData, member_lname: e.target.value })}
                          required={newExtensionData.create_member}
                          size="small"
                        />
                      </Grid>
                    </Grid>
                    
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={newExtensionData.member_email}
                      onChange={(e) => setNewExtensionData({ ...newExtensionData, member_email: e.target.value })}
                      required={newExtensionData.create_member}
                      size="small"
                      sx={{ mt: 1 }}
                    />
                    
                    <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                      <InputLabel>User Type</InputLabel>
                      <Select
                        value={newExtensionData.member_type}
                        onChange={(e) => setNewExtensionData({ ...newExtensionData, member_type: e.target.value })}
                        label="User Type"
                      >
                        <MenuItem value="member">Regular Member</MenuItem>
                        <MenuItem value="admin">Admin</MenuItem>
                      </Select>
                    </FormControl>
                  </>
                )}
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAddDialogOpen(false);
            setNewExtensionData({
              extension: '',
              active: true,
              create_member: false,
              member_fname: '',
              member_lname: '',
              member_email: '',
              member_type: 'member',
              timezone: 'eastern',
              directory: true,
              rings: 4,
              cell_number: '',
              ring_cell: false,
              outgoing_id: '',
              site: '',
              department: ''
            });
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddExtension}
            disabled={!newExtensionData.extension || loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Create Extension'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Extension Dialog */}
      <Dialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Assign Extension {selectedExtension?.extension}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Select a member to assign this extension to:
            </Typography>
            
            {availableMembers.length === 0 ? (
              <Alert severity="info">
                No unassigned members available. All members already have extensions assigned.
              </Alert>
            ) : (
              <FormControl fullWidth>
                <InputLabel>Select Member</InputLabel>
                <Select
                  value={selectedMemberId}
                  onChange={(e) => setSelectedMemberId(e.target.value)}
                  label="Select Member"
                >
                  <MenuItem value="">
                    <em>-- Select Member --</em>
                  </MenuItem>
                  {availableMembers.map((member) => (
                    <MenuItem key={member.mem_id} value={member.mem_id}>
                      {member.mem_lname}, {member.mem_fname} ({member.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)} disabled={assignLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleAssignSubmit} 
            variant="contained" 
            color="success"
            disabled={!selectedMemberId || assignLoading}
          >
            {assignLoading ? <CircularProgress size={20} /> : 'Assign Extension'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Site/Department Management Dialog */}
      <Dialog
        open={siteDepartmentDialogOpen}
        onClose={() => setSiteDepartmentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Manage Sites & Departments</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Current Extension Site/Department */}
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Current Extension Settings
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Site</InputLabel>
                  <Select
                    value={editDialogOpen ? (formData.site || '') : (newExtensionData.site || '')}
                    onChange={(e) => {
                      if (editDialogOpen) {
                        setFormData({ ...formData, site: e.target.value });
                      } else {
                        setNewExtensionData({ ...newExtensionData, site: e.target.value });
                      }
                    }}
                    label="Site"
                  >
                    <MenuItem value="">None</MenuItem>
                    {sites.map((site) => (
                      <MenuItem key={site.id} value={site.name}>{site.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Department</InputLabel>
                  <Select
                    value={editDialogOpen ? (formData.department || '') : (newExtensionData.department || '')}
                    onChange={(e) => {
                      if (editDialogOpen) {
                        setFormData({ ...formData, department: e.target.value });
                      } else {
                        setNewExtensionData({ ...newExtensionData, department: e.target.value });
                      }
                    }}
                    label="Department"
                  >
                    <MenuItem value="">None</MenuItem>
                    {departments.map((dept) => (
                      <MenuItem key={dept.id} value={dept.name}>{dept.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            {/* Add New Site */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Add New Site
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Site Name"
                value={newSite}
                onChange={(e) => setNewSite(e.target.value)}
                onKeyPress={async (e) => {
                  if (e.key === 'Enter' && newSite.trim()) {
                    if (!sites.find(s => s.name === newSite.trim())) {
                      try {
                        const params = getCustomerParams();
                        const url = createUrlWithCustomerParams('/endpoints/sites_departments/sites', params);
                        const response = await api.post(url, { name: newSite.trim() });
                        if (response.data.success) {
                          setSites([...sites, { id: response.data.data.id, name: response.data.data.name }]);
                          setNewSite('');
                        }
                      } catch (err: any) {
                        setError(err.response?.data?.message || 'Failed to add site');
                      }
                    }
                  }
                }}
              />
              <Button
                variant="outlined"
                onClick={async () => {
                  if (newSite.trim() && !sites.find(s => s.name === newSite.trim())) {
                    try {
                      const response = await api.post('/endpoints/sites_departments/sites', { name: newSite.trim() });
                      if (response.data.success) {
                        setSites([...sites, { id: response.data.data.id, name: response.data.data.name }]);
                        setNewSite('');
                      }
                    } catch (err: any) {
                      setError(err.response?.data?.message || 'Failed to add site');
                    }
                  }
                }}
                disabled={!newSite.trim() || sites.some(s => s.name === newSite.trim())}
              >
                Add
              </Button>
            </Box>

            {/* Add New Department */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Add New Department
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Department Name"
                value={newDepartment}
                onChange={(e) => setNewDepartment(e.target.value)}
                onKeyPress={async (e) => {
                  if (e.key === 'Enter' && newDepartment.trim()) {
                    if (!departments.find(d => d.name === newDepartment.trim())) {
                      try {
                        const params = getCustomerParams();
                        const url = createUrlWithCustomerParams('/endpoints/sites_departments/departments', params);
                        const response = await api.post(url, { name: newDepartment.trim() });
                        if (response.data.success) {
                          setDepartments([...departments, { id: response.data.data.id, name: response.data.data.name }]);
                          setNewDepartment('');
                        }
                      } catch (err: any) {
                        setError(err.response?.data?.message || 'Failed to add department');
                      }
                    }
                  }
                }}
              />
              <Button
                variant="outlined"
                onClick={async () => {
                  if (newDepartment.trim() && !departments.find(d => d.name === newDepartment.trim())) {
                    try {
                      const response = await api.post('/endpoints/sites_departments/departments', { name: newDepartment.trim() });
                      if (response.data.success) {
                        setDepartments([...departments, { id: response.data.data.id, name: response.data.data.name }]);
                        setNewDepartment('');
                      }
                    } catch (err: any) {
                      setError(err.response?.data?.message || 'Failed to add department');
                    }
                  }
                }}
                disabled={!newDepartment.trim() || departments.some(d => d.name === newDepartment.trim())}
              >
                Add
              </Button>
            </Box>

            {/* Existing Sites List */}
            {sites.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Existing Sites
                </Typography>
                <Box sx={{ mb: 2 }}>
                  {sites.map((site) => (
                    <Chip
                      key={site.id}
                      label={site.name}
                      onDelete={async () => {
                        try {
                          const params = getCustomerParams();
                          const url = createUrlWithCustomerParams(`/endpoints/sites_departments/sites/${site.id}`, params);
                          const response = await api.delete(url);
                          if (response.data.success) {
                            setSites(sites.filter(s => s.id !== site.id));
                          }
                        } catch (err: any) {
                          setError(err.response?.data?.message || 'Failed to delete site');
                        }
                      }}
                      size="small"
                      sx={{ mr: 0.5, mb: 0.5 }}
                    />
                  ))}
                </Box>
              </>
            )}

            {/* Existing Departments List */}
            {departments.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Existing Departments
                </Typography>
                <Box>
                  {departments.map((dept) => (
                    <Chip
                      key={dept.id}
                      label={dept.name}
                      onDelete={async () => {
                        try {
                          const params = getCustomerParams();
                          const url = createUrlWithCustomerParams(`/endpoints/sites_departments/departments/${dept.id}`, params);
                          const response = await api.delete(url);
                          if (response.data.success) {
                            setDepartments(departments.filter(d => d.id !== dept.id));
                          }
                        } catch (err: any) {
                          setError(err.response?.data?.message || 'Failed to delete department');
                        }
                      }}
                      size="small"
                      sx={{ mr: 0.5, mb: 0.5 }}
                    />
                  ))}
                </Box>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSiteDepartmentDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Charge Preview Dialog */}
      <ChargePreviewDialog
        open={chargePreviewOpen}
        onClose={handleChargePreviewClose}
        onConfirm={handleChargePreviewConfirm}
        onAddFunds={handleAddFunds}
        chargeDetails={chargeDetails}
        loading={chargePreviewLoading}
        error={chargePreviewError}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="error" />
          Delete Extension {selectedExtension?.extension}
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {/* CRITICAL: Show which customer will be affected */}
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                This will delete extension {selectedExtension?.extension} from Customer ID: {
                  (() => {
                    const params = getCustomerParams();
                    const targetCustomerId = params.customerId || member?.customerID;
                    return targetCustomerId || 'UNKNOWN - OPERATION BLOCKED';
                  })()
                }
              </Typography>
              {isLoginAsCustomer && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                  (Login-as mode active: {(member as any)?.loginAsCustomerName || 'Customer'})
                </Typography>
              )}
            </Alert>

            {selectedExtension && !selectedExtension.is_unassigned && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  This extension is currently assigned to {selectedExtension.name}
                </Typography>
              </Alert>
            )}
            
            <Typography variant="body1" sx={{ mb: 2 }}>
              This action will:
            </Typography>
            
            <Box component="ul" sx={{ pl: 2, mb: 2 }}>
              {selectedExtension && !selectedExtension.is_unassigned && (
                <li>
                  <Typography variant="body2">
                    Unassign the extension from {selectedExtension.name}
                  </Typography>
                </li>
              )}
              <li>
                <Typography variant="body2">
                  Delete all voicemail messages for this extension
                </Typography>
              </li>
              {selectedExtension?.has_softphone && (
                <li>
                  <Typography variant="body2">
                    Permanently delete the associated softphone account
                  </Typography>
                </li>
              )}
              <li>
                <Typography variant="body2">
                  Remove all forwarding rules and call routing
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Delete SIP credentials from the system
                </Typography>
              </li>
            </Box>
            
            <Alert severity="error">
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                This action cannot be undone!
              </Typography>
            </Alert>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained" disabled={loading}>
            Delete Extension
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExtensionsV2;